import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, DatePicker, Modal, Row, Select, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useI18n } from '../i18n';
import { isAllowedCertificateFile } from '../utils/certificateHelpers';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Existing certs use free-text documentType values (e.g. "Textile Safety"),
// so this stays a taggable Select rather than a closed enum — these are just
// the presets, including the 3 standardized types from product-module-
// naming.md §7.2 (Test Report / Material Declaration / REACH Declaration).
const DOCUMENT_TYPE_PRESETS = [
  'Quality Management',
  'Textile Safety',
  'Forest Stewardship Council',
  'Environmental Management',
  'Food Contact Safety',
  'Chemical Compliance',
  'Electronics Safety',
  'Product Safety',
  'Occupational Health & Safety',
  'Organic Textile',
  'Leather Sourcing',
  'Customs Compliance',
  'Road Safety Management',
  'Social Compliance',
  'Test Report',
  'Material Declaration',
  'REACH Declaration',
];

export default function CertificateFormModal({
  open,
  onCancel,
  onSubmit,
  editingCertificate = null,
  supplier,
  products = [],
  currentUser,
}) {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const isEditing = Boolean(editingCertificate);
  const scope = Form.useWatch('scope', form);

  const [fileList, setFileList] = useState([]);
  const [existingFile, setExistingFile] = useState(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editingCertificate?.name || '',
      description: editingCertificate?.description || '',
      documentType: editingCertificate?.documentType ? [editingCertificate.documentType] : [],
      standard: editingCertificate?.standard || '',
      scope: editingCertificate?.scope || (editingCertificate?.productId != null ? 'Product' : 'Supplier'),
      productId: editingCertificate?.productId ?? null,
      validFrom: editingCertificate?.validFrom ? dayjs(editingCertificate.validFrom) : null,
      expiryDate: editingCertificate?.expiryDate ? dayjs(editingCertificate.expiryDate) : null,
    });
    setFileList([]);
    setExistingFile(editingCertificate
      ? { fileName: editingCertificate.fileName, fileUrl: editingCertificate.fileUrl }
      : null);
    setFileError('');
  }, [editingCertificate, form, open]);

  const beforeUpload = (file) => {
    if (!isAllowedCertificateFile(file)) {
      setFileError(t('File must be one of .pdf, .png, .jpg, .jpeg, .doc, .docx and at most 10 MB'));
      return false;
    }
    setFileError('');
    setFileList([file]);
    return false;
  };

  const handleRemoveFile = () => {
    setFileList([]);
  };

  const handleFinish = async () => {
    const values = await form.validateFields();

    if (fileList.length === 0 && !existingFile) {
      setFileError(t('A file is required'));
      return;
    }

    const newFile = fileList[0] || null;
    const certData = {
      ...(editingCertificate || {}),
      supplierId: supplier?.id,
      name: values.name.trim(),
      description: values.description || '',
      documentType: (Array.isArray(values.documentType) ? values.documentType[0] : values.documentType) || '',
      standard: values.standard || '',
      scope: values.scope,
      productId: values.scope === 'Product' ? (values.productId ?? null) : null,
      validFrom: values.validFrom ? values.validFrom.format('YYYY-MM-DD') : null,
      expiryDate: values.expiryDate.format('YYYY-MM-DD'),
      ...(newFile
        ? {
          fileName: newFile.name,
          fileUrl: URL.createObjectURL(newFile),
          uploadedBy: currentUser?.name || 'System',
          uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
        }
        : {}),
    };

    const success = onSubmit(certData);
    if (success !== false) {
      setFileList([]);
      setFileError('');
    }
  };

  const currentFileLabel = fileList[0]?.name || existingFile?.fileName;

  return (
    <Modal
      title={isEditing ? t('Edit Certificate') : t('Add Certificate')}
      open={open}
      onCancel={onCancel}
      width={560}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleFinish}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('Name')}
          name="name"
          rules={[
            { required: true, message: t('Name is required') },
            {
              validator: (_, value) => (
                value && value.trim().length === 0
                  ? Promise.reject(new Error(t('Name is required')))
                  : Promise.resolve()
              ),
            },
            { max: 255, message: t('Name must be at most 255 characters') },
          ]}
        >
          <Input placeholder={t('Certificate name')} />
        </Form.Item>
        <Form.Item label={t('Description')} name="description">
          <TextArea rows={3} placeholder={t('Description')} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('Document Type')}
              name="documentType"
              rules={[{ required: true, message: t('Document type is required') }]}
            >
              <Select
                showSearch
                mode="tags"
                maxCount={1}
                placeholder={t('e.g. Quality Management')}
                options={DOCUMENT_TYPE_PRESETS.map((type) => ({ value: type, label: type }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('Standard')}
              name="standard"
              rules={[{ required: true, message: t('Standard is required') }]}
            >
              <Input placeholder={t('e.g. ISO 9001')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('Scope')}
              name="scope"
              rules={[{ required: true, message: t('Please select scope') }]}
            >
              <Select
                onChange={(value) => {
                  if (value === 'Supplier') form.setFieldsValue({ productId: null });
                }}
              >
                <Option value="Supplier">{t('Supplier')}</Option>
                <Option value="Product">{t('Product')}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('Product')}
              name="productId"
              rules={scope === 'Product' ? [{ required: true, message: t('Please select product') }] : []}
            >
              <Select
                showSearch
                optionFilterProp="children"
                disabled={scope !== 'Product'}
                placeholder={scope === 'Product' ? t('Select product') : t('Not applicable — supplier-wide')}
                options={products.map((product) => ({ value: product.id, label: product.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('Valid From')}
              name="validFrom"
              rules={[{ required: true, message: t('Valid from date is required') }]}
            >
              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('Valid To')}
              name="expiryDate"
              rules={[
                { required: true, message: t('Valid to date is required') },
              ]}
            >
              <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={t('File Upload')} required>
          <Upload
            beforeUpload={beforeUpload}
            onRemove={handleRemoveFile}
            fileList={fileList}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
          </Upload>
          {!fileList.length && existingFile && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {t('Current file')}: {currentFileLabel}
            </Text>
          )}
          {fileError && (
            <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {fileError}
            </Text>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
}
